import React, { FC } from 'react';
import {
  BasicContainer,
  BodyText,
  ButtonAsWrapper,
  FlexCol,
  FlexDynamic,
  FlexRow,
  TextDynamic,
} from '@minecraft.atoms';
import Icon from '@minecraft.icon';
import { appConfig } from '@minecraft.app-config';
import { useTranslation } from '@blocs.i18n';
import { captureHeapEvent, HEAP_EVENT_NAMES } from '@blocs.heap';
import { useRouter } from '@blocs.router';

export const TRIAL_PROFILE_READY_BANNER_BG = `${appConfig.cdnRoot}/images/static/banner-source.jpg`;

interface TrialProfileReadyBannerProps {
  profileUrl: string;
}

export const TrialProfileReadyBanner: FC<TrialProfileReadyBannerProps> = ({ profileUrl }) => {
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const ctaLabel = t('common:paywall.trial.profileReadyBanner.cta');

  return (
    <ButtonAsWrapper
      unsetSizing
      noPadding
      className="cn_w-full"
      aria-label={ctaLabel}
      data-testid="paywall-trial-profile-ready-banner"
      data-heap-id="global-banner-review-profile"
      onClick={() => {
        captureHeapEvent({ name: HEAP_EVENT_NAMES.GLOBAL_PROFILE_BANNER_CTA_CLICKED });
        navigate({ to: profileUrl });
      }}
    >
      <BasicContainer
        w="full"
        backgroundImage={{
          image: TRIAL_PROFILE_READY_BANNER_BG,
          size: 'cover',
          position: 'center',
          repeat: 'no-repeat',
        }}
        mdProps={{
          backgroundImage: {
            image: TRIAL_PROFILE_READY_BANNER_BG,
            size: 'cover',
            position: 'top',
            repeat: 'no-repeat',
          },
        }}
        className="cn_relative cn_overflow-hidden"
      >
        <FlexDynamic
          w="full"
          bgColor="maxContrast"
          bgOpacity={0.4}
          direction="column"
          alignItems="stretch"
          gap="3"
          className="cn_atom_px-4 cn_atom_py-4"
          mdProps={{
            direction: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8',
            className: 'cn_atom_px-8 cn_atom_py-6',
          }}
        >
          <FlexCol gap="2">
            <TextDynamic
              color="inverted"
              fontSize="h3"
              fontWeight="semibold"
              mdProps={{ fontSize: 'h2', fontWeight: 'medium' }}
            >
              {t('common:paywall.trial.profileReadyBanner.title')}
            </TextDynamic>
            <TextDynamic color="inverted" fontSize="bodySmall" mdProps={{ fontSize: 'bodyRegular' }}>
              {t('common:paywall.trial.profileReadyBanner.subtitle')}
            </TextDynamic>
          </FlexCol>
          {/* Decorative CTA — not interactive; navigation is handled by ButtonAsWrapper */}
          <BasicContainer
            bgColor="goldPaywallSuccessBg"
            borderRadius="sm"
            w="full"
            mdProps={{ w: 'auto' }}
            className="cn_atom_px-4 cn_atom_py-2 cn_pointer-events-none cn_shrink-0"
            aria-hidden
            data-testid="paywall-trial-profile-ready-banner-cta"
          >
            <FlexRow alignItems="center" justifyContent="center" gap="2">
              <BodyText fontWeight="semibold" color="default">
                {ctaLabel}
              </BodyText>
              <Icon name="chevron-right-thick" size="sm" />
            </FlexRow>
          </BasicContainer>
        </FlexDynamic>
      </BasicContainer>
    </ButtonAsWrapper>
  );
};
